// ADR 23.9.26u §8, §12 — Chromium's `beforeinstallprompt`, captured and held
// so the account menu's "Install app" is the one affordance, and the
// `appinstalled` that follows it counted. Since 25.9.26h §4 the same store
// also holds whether `getInstalledRelatedApps` found this app installed, so an
// installed learner revisiting in a tab is not shown steps for it.
//
// `startInstallCapture` runs synchronously at the top of `bootstrap()`, before
// any `await`: the event can fire early in the page's life, and a listener
// attached after it has fired never hears it.

import { reportPromptFailed } from '../telemetry/field/installReports'
import { emitInstallPromptResult, emitPwaInstalled, type InstallSurface } from '../telemetry/pwa'
import { installRequested } from './installDeepLink'
import type { Platform } from './installAffordance'

/** The non-standard event, reduced to the members used. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** 25.9.26h §4 — the Chromium method, absent from `lib.dom`, reduced to what is read. */
interface RelatedAppsNavigator {
  getInstalledRelatedApps?: () => Promise<readonly unknown[]>
}

/**
 * What `startInstallCapture` reads. `navigator` is optional and typed loosely
 * so an `EventTarget` stands in for `window` in tests that need no probe;
 * `location` likewise (6.10.26a §5), defaulting to the window's.
 */
type InstallCaptureTarget = Pick<Window, 'addEventListener'> & {
  navigator?: object
  location?: Pick<Location, 'search'>
}

/**
 * ADR 6.10.26a §5 — an `?install=1` link's request: `pending` from boot,
 * `open` once the strip has acted on it (exactly once), `none` once closed.
 */
export type InstallRequest = 'none' | 'pending' | 'open'

let deferred: BeforeInstallPromptEvent | null = null
let acceptedHere = false
// ADR 15.10.26d §4 — the platform whose steps (with their feedback control) were shown in this page.
let stepsShownOn: Platform | null = null
let installedElsewhereFlag = false
let request: InstallRequest = 'none'
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/**
 * 25.9.26h §4 — ask once whether this app is installed. Absent, throwing or
 * rejecting all leave the flag `false`: the check fails open to showing steps.
 */
function probeInstalledElsewhere(nav: object | undefined): void {
  const related = nav as RelatedAppsNavigator | undefined
  if (typeof related?.getInstalledRelatedApps !== 'function') return
  const found = (apps: readonly unknown[]) => {
    if (apps.length === 0) return
    installedElsewhereFlag = true
    notify()
  }
  try {
    related.getInstalledRelatedApps().then(found, () => undefined)
  } catch {
    // A synchronous throw is the same answer as an absent method.
  }
}

/**
 * Listen for `beforeinstallprompt` (suppressing Chrome's mini-infobar) and
 * `appinstalled`, and start the installed-elsewhere probe.
 */
export function startInstallCapture(win: InstallCaptureTarget): void {
  win.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })
  win.addEventListener('appinstalled', () => {
    // ADR 15.10.26d §4 — `prompt` > `steps` > `browser`; only `steps` names its platform.
    if (acceptedHere) emitPwaInstalled('prompt')
    else if (stepsShownOn !== null) emitPwaInstalled('steps', stepsShownOn)
    else emitPwaInstalled('browser')
    acceptedHere = false
    stepsShownOn = null
    deferred = null
    notify()
  })
  probeInstalledElsewhere(win.navigator)
  // On a first load this runs before `enterBoot`'s rewrite, so the raw
  // parameter is visible; the `next` reading covers a reload of the rewrite.
  const search = (win.location ?? globalThis.location)?.search ?? ''
  if (installRequested(search)) request = 'pending'
}

/** For `useSyncExternalStore`. */
export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function promptAvailable(): boolean {
  return deferred !== null
}

/** 25.9.26h §4 — a second snapshot of the same store; a boolean, so stable. */
export function installedElsewhere(): boolean {
  return installedElsewhereFlag
}

/**
 * ADR 15.10.26d §4 — the steps panel showed its *These steps didn't work*
 * control for `platform`, so an `appinstalled` that follows counts as `steps`.
 * Idempotent: StrictMode's double effect writes the same value twice.
 */
export function markStepsShown(platform: Platform): void {
  stepsShownOn = platform
}

/**
 * Show the browser's install dialogue. The spec lets a prompt be used once, so
 * it is dropped after; the capture listener stays attached, so a browser that
 * fires `beforeinstallprompt` again re-arms it. 6.10.26a §4 — the answer is
 * reported with the CTA that asked.
 *
 * ADR 15.10.26d §3 — a rejecting `prompt()` or `userChoice` is reported as
 * `install.prompt_failed` with the browser's own error, then swallowed, so the
 * three `void promptInstall(...)` call sites never see an unhandled rejection.
 */
export async function promptInstall(surface: InstallSurface): Promise<void> {
  const event = deferred
  if (event === null) return
  deferred = null
  notify()
  let outcome: 'accepted' | 'dismissed'
  try {
    await event.prompt()
    outcome = (await event.userChoice).outcome
  } catch (error) {
    await reportPromptFailed(error, surface)
    return
  }
  if (outcome === 'accepted') acceptedHere = true
  emitInstallPromptResult(outcome, surface)
}

/** 6.10.26a §5 — a snapshot for `useSyncExternalStore`; a string, so stable. */
export function installRequest(): InstallRequest {
  return request
}

/**
 * `pending → open`, returning `true` only on that transition. Effects double-run
 * under StrictMode, so the second call returns `false` and changes nothing.
 */
export function openInstallRequest(): boolean {
  if (request !== 'pending') return false
  request = 'open'
  notify()
  return true
}

/** The strip closed (× or an install): the slot falls back to its consent order. */
export function closeInstallRequest(): void {
  if (request === 'none') return
  request = 'none'
  notify()
}

/** Tests only: the store is module-level. */
export function resetForTests(): void {
  deferred = null
  acceptedHere = false
  stepsShownOn = null
  installedElsewhereFlag = false
  request = 'none'
  listeners.clear()
}
